import React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Dropdown } from 'react-bootstrap';
import DateTime from './DateTime';
import chinese from '../../audio/chinese.mp3';
import garden from '../../audio/garden.mp3';
import zen from '../../audio/zen.mp3';
import relaxing from '../../audio/relaxing.mp3';
import now from '../../audio/now.mp3';

export const PLAYLIST = [
  { id: 1, title: 'Chinese', src: chinese },
  { id: 2, title: 'Garden', src: garden },
  { id: 3, title: 'Zen', src: zen },
  { id: 4, title: 'Relaxing', src: relaxing },
  { id: 5, title: 'Now', src: now }
];

const formatTime = (time) => {
  if (isNaN(time) || time < 0) return '00:00';
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = Math.floor(time % 60);

  const paddedMinutes = minutes.toString().padStart(2, '0');
  const paddedSeconds = seconds.toString().padStart(2, '0');

  // Si plus d'une heure, format hh:mm:ss
  if (hours > 0) {
    const paddedHours = hours.toString().padStart(2, '0');
    return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
  }

  // Sinon, format mm:ss
  return `${paddedMinutes}:${paddedSeconds}`;
};

const useAudio = (playlist) => {
  const { isMute, currentTrack, duration, isLoop, isPlaylistLoop } = useSelector((state) => state.player);
  const dispatch = useDispatch();

  const [trackIndex, setTrackIndex] = useState(() => {
    const savedIndex = localStorage.getItem('audio_track_index');

    if (savedIndex !== null) {
      const parsed = parseInt(savedIndex, 10);
      if (!isNaN(parsed) && parsed >= 0 && parsed < playlist.length) return parsed;
    }
    return 0;
  });

  const [volume, setVolume] = useState(() => {
    const savedVolume = localStorage.getItem('audio_volume');
    if (savedVolume !== null) {
      const parsed = parseInt(savedVolume, 10);
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) return parsed;
    }
    return 50;
  });

  const [currentTime, setCurrentTime] = useState(0);
  const playerRef = useRef(null);

  // On écoute si l'AudioEngine demande le morceau suivant
  useEffect(() => {
  // Si le morceau s'est terminé et qu'on doit passer au suivant en boucle playlist
  // On réutilise la fonction handleNext() existante !
    if (isPlaylistLoop && currentTrack && currentTime >= duration - 0.5) {
    // Une sécurité : si l'engine n'a pas encore changé, on peut laisser handleNext s'en charger
    }
  }, [isPlaylistLoop, currentTrack, currentTime, duration]);

// Pour simplifier, ajoutons la gestion de l'action directement dans le reducer ou dans useAudio.
// Le plus simple et le plus propre est d'ajouter le cas 'NEXT_TRACK' dans votre Reducer !

  // Utilisation de useCallback pour stabiliser handleNext et l'inclure dans les dépendances
  const handleNext = useCallback(() => {
    setTrackIndex((prevIndex) => {
      const nextIndex = (prevIndex + 1) % playlist.length;

      if (playlist[nextIndex]) {
        dispatch({
          type: 'SET_TRACK',
          payload: { id: playlist[nextIndex].id, title: playlist[nextIndex].title, url: playlist[nextIndex].src }
        });
      }
      return nextIndex;
    });
  }, [playlist, dispatch]);

  const handlePrev = useCallback(() => {
    setTrackIndex((prevIndex) => {
      const nextIndex = (prevIndex - 1 + playlist.length) % playlist.length;

      // 🚀 On met Redux à jour immédiatement avec la piste précédente !
      if (playlist[nextIndex]) {
        dispatch({
          type: 'SET_TRACK',
          payload: { id: playlist[nextIndex].id, title: playlist[nextIndex].title, url: playlist[nextIndex].src }
        });
      }
      return nextIndex;
    });
  }, [playlist, dispatch]);


  // 🟢 2. EFFETS POUR SAUVEGARDER DANS LE LOCALSTORAGE
  useEffect(() => {
    localStorage.setItem('audio_track_index', trackIndex.toString());
  }, [trackIndex]);

  useEffect(() => {
    localStorage.setItem('audio_volume', volume.toString());
  }, [volume]);

  const togglePlay = () => {
    if (!currentTrack && playlist && playlist[trackIndex]) {
      dispatch({
        type: 'SET_TRACK',
        payload: {
          id: playlist[trackIndex].id,
          title: playlist[trackIndex].title,
          url: playlist[trackIndex].src || playlist[trackIndex].url
          // url: playlist[trackIndex].src
        }
      });
    } else {
      // 🔄 SINON : On se contente d'alterner normalement entre Play et Pause
      dispatch({ type: 'TOGGLE_PLAY' }); // ⚡ Envoie l'ordre Play/Pause au moteur racine
    }
  };

  const toggleLoop = () => {
    dispatch({ type: 'TOGGLE_LOOP' }); // ⚡ Envoie l'ordre de bouclage au moteur racine Redux
  };

  const togglePlaylistLoop = () => {
    dispatch({ type: 'TOGGLE_PLAYLIST_LOOP' });
  };

  const toggleMute = () => {
    dispatch({ type: 'TOGGLE_MUTE' }); // ⚡ Envoie l'ordre de couper/remettre le son
  };

  const handleStop = () => {
    // 1. On remet à zéro l'affichage de la barre de progression locale
    setCurrentTime(0);
    dispatch({ type: 'STOP_TRACK' }); // ⚡ Envoie l'ordre d'arrêt total
  };

  const handleVolumeChange = (e) => {
    const value = Number(e.target.value);
    setVolume(value);

    // 🚀 On envoie le nouveau volume à Redux pour que l'AudioEngine l'applique !
    dispatch({ type: 'SET_VOLUME', payload: value });

     // 🎯 Si on monte le son alors qu'on était muet, on réactive le son
    if (value > 0 && isMute) {
      dispatch({ type: 'TOGGLE_MUTE' }); // Sort du mode muet
    }

    // 🎯 Si le volume tombe à zéro et que l'app n'est pas déjà muette, on dispatch !
    if (value === 0 && !isMute) {
      dispatch({ type: 'TOGGLE_MUTE' });
    }
  };

  // Nouvelle fonction pour modifier le positionnement du morceau (Seek)
  const handleProgressChange = (e) => {
    const newTime = Number(e.target.value);
    setCurrentTime(newTime);

    // 🚀 On demande à Redux de déplacer la tête de lecture dans l'AudioEngine !
    dispatch({ type: 'SET_SEEK_TIME', payload: newTime });
  };

  const selectTrack = (audioPiste) => {
    if (!audioPiste) return;

    // 🎯 1. On trouve l'index numérique correspondant dans le tableau pour le hook local
    const indexTrouve = playlist.findIndex(t => t.id === audioPiste.id);

    if (indexTrouve !== -1) {
      setTrackIndex(indexTrouve); // Met à jour l'index physique (0, 1...)

      // 🚀 CLIC VOLONTAIRE : Ici, on dispatch immédiatement à Redux pour lancer le morceau !
      dispatch({
        type: 'SET_TRACK',
        payload: {
          id: audioPiste.id,
          title: audioPiste.title,
          url: audioPiste.src || audioPiste.url
        }
      });
    }
  };

  return {
    togglePlay,
    handleStop,
    isLoop,
    isPlaylistLoop,
    toggleLoop,
    togglePlaylistLoop,
    isMute,
    toggleMute,
    handleVolumeChange,
    volume,
    trackIndex,
    handleNext,
    handlePrev,
    selectTrack,
    playerRef,
    currentTime,
    handleProgressChange
  };
};

const SoundSwitcher = () => {
  const { isPlaying, currentTrack, isMute, currentTime, duration } = useSelector((state) => state.player);
  const dispatch = useDispatch();
  const [inputUrl, setInputUrl] = useState('');

  const {
    togglePlay,
    handleStop,
    isLoop,
    isPlaylistLoop,
    toggleLoop,
    togglePlaylistLoop,
    toggleMute,
    handleVolumeChange,
    volume,
    handleNext,
    handlePrev,
    selectTrack,
    playerRef,
    handleProgressChange
  } = useAudio(PLAYLIST);

  // 🎯 LA FONCTION DE LANCEMENT DE L'URL INTERNET
  const handlePlayUrl = (e) => {
    e.preventDefault();
    if (!inputUrl.trim()) return;

    dispatch({
      type: 'SET_TRACK',
      payload: {
        id: `url_${Date.now()}`, // Génère un identifiant unique
        title: "Flux Audio Externe",
        url: inputUrl.trim() // Ton lien internet .mp3
      }
    });

    setInputUrl(''); // Vide le champ après validation
  };

  return (
    <div ref={playerRef} className="w-100 text-warning">
      <DateTime />

      <div className="current-track my-3 text-center">
        <p className="text-secondary fw-bold m-0 mb-2">🎵 Contrôle Audio</p>
        <span className="text-dark-50 small mb-1 italic">
          {currentTrack ? `En écoute : ${currentTrack.title}` : "Aucune piste sélectionnée"}
        </span>
      </div>

      <div className="progress-container px-2 mb-3">
        <div className="d-flex justify-content-between small text-muted mb-1">
          <span className="text-secondary fw-bold">{formatTime(currentTime)}</span>
          <span className="text-secondary fw-bold">{formatTime(duration)}</span>
        </div>
        <input
          type="range"
          className="form-range progress-bar-input"
          value={currentTime || 0}
          min={0}
          max={duration || 100}
          step={0.1}
          onChange={handleProgressChange}
          style={{ width: '100%', cursor: 'pointer' }}
        />
      </div>

      <div className="d-flex gap-3 justify-content-center align-items-center mb-3">
        {/* Bouton Précédent */}
        <div onClick={handlePrev} className="text-secondary" style={{ cursor: 'pointer' }} title="Précédent">
          <i className='material-icons fs-3'>skip_previous</i>
        </div>

        {/* Bouton Play / Pause branché sur Redux */}
        <div variant="warning" onClick={togglePlay} disabled={!currentTrack} style={{ cursor: 'pointer' }}>
          {isPlaying ? (
            <i className='material-icons'>pause</i>
          ) : (
              <i className='material-icons'>play_arrow</i>
            )}
        </div>

        {/* Bouton Suivant */}
        <div onClick={handleNext} className="text-secondary" style={{ cursor: 'pointer' }} title="Suivant">
          <i className='material-icons fs-3'>skip_next</i>
        </div>

        {/* Bouton Stop */}
        <div onClick={handleStop} className="text-danger" style={{ cursor: 'pointer' }} title="Arrêter">
          <i className='material-icons fs-3'>stop</i>
        </div>

        {/* Bouton Boucle */}
        <div onClick={toggleLoop} className={isLoop ? "text-success" : "text-muted"} style={{ cursor: 'pointer' }} title="Rejouer le titre">
          <i className='material-icons fs-4'>{isLoop ? 'repeat_one' : 'repeat'}</i>
        </div>

        <div onClick={togglePlaylistLoop} className={`${isPlaylistLoop ? 'text-warning' : 'text-muted'}`}
          style={{ cursor: 'pointer' }} title="Playlist en boucle">
          <i className="material-icons">repeat_on</i>
        </div>
      </div>

      {/* 🔽 LE MENU DROPDOWN DE LA PLAYLIST */}
      <div className="dropdown-playlist-container px-2 mb-3 text-center">
        <Dropdown className="w-100">

          {/* Le bouton principal du Dropdown : Il affiche dynamiquement le titre actif */}
          <Dropdown.Toggle
            variant="outline-warning"
            id="dropdown-custom-components"
            className="w-100 text-truncate fw-bold btn-sm d-flex align-items-center justify-content-between px-3"
          >
            <span className="d-flex align-items-center gap-1 fw-semibold">
              <i className="material-icons fs-6">library_music</i>
              {currentTrack ? currentTrack.title : "Choisir une piste..."}
            </span>
          </Dropdown.Toggle>

          {/* La liste déroulante des morceaux */}
          <Dropdown.Menu className="dropdown-menu-dark w-100 shadow-lg border-secondary border-opacity-50">
            {PLAYLIST.map((track) => {
              const estActif = currentTrack?.title === track.title;

              return (
                <Dropdown.Item
                  key={track.id}
                  onClick={() => selectTrack(track)}
                  active={estActif}
                  className={`d-flex align-items-center gap-1 small py-2 ${estActif ? 'bg-warning text-dark fw-semibold' : 'text-light'}`}
                >
                  <i className="material-icons fs-6">
                    {estActif ? 'volume_up' : 'music_note'}
                  </i>
                  {track.title}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="volume-container px-2 border-top pt-2">
        <div className="d-flex align-items-center gap-4 justify-content-center">
          <button
            type="button"
            className="btn btn-link p-0 text-decoration-none d-flex align-items-center"
            onClick={toggleMute} // ⚡ Déclenche l'action Redux globale !
            style={{ border: 'none', background: 'transparent' }}
            title={isMute ? "Activer le son" : "Couper le son"}
          >
            <i className={`material-icons fs-3 ${isMute ? 'text-danger' : 'text-warning'}`}>
              {isMute ? 'volume_off' : volume === 0 ? 'volume_mute' : volume < 50 ? 'volume_down' : 'volume_up'}
            </i>
          </button>
          <input
            type='range'
            className='form-range'
            value={isMute ? 0 : volume}
            min={0}
            max={100}
            step={1}
            onChange={handleVolumeChange}
            style={{ maxWidth: '130px', cursor: 'pointer' }}
          />
          <span className="small text-white style-white" style={{ minWidth: '35px' }}>
            {isMute ? '0' : volume}%
          </span>
        </div>
      </div>

      {/* 🚀 FORMULAIRE MAGIQUE POUR LES LIENS AUDIO EXTERNES */}
      <div className="px-1 mt-3 pt-3 border-top border-secondary border-opacity-25">
        <form onSubmit={handlePlayUrl}>
          <div className="input-group input-group-sm">
            <input
              type="url"
              placeholder="Coller un lien mp3..."
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              className="form-control bg-secondary text-light border-secondary border-opacity-50"
            />
            <button className="btn btn-warning fw-semibold px-2" type="submit">
              ⚡ Lancer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SoundSwitcher;